import React, { useMemo, useState } from "react";
import "../features.css";
import API from "../../utils/API";
import { FEATURES } from "../../config/features";
import { useAuth } from "../../auth/AuthProvider";
import { useBookData } from "../../data/BookDataProvider";
import { formatDate } from "../../data/format";
import {
  Badge,
  Button,
  Card,
  Combobox,
  EmptyState,
  Field,
  Segmented,
  Select,
  TextArea,
  useToast,
  ServiceIcon,
  PlusIcon
} from "../../ui";
import { SR_TYPES, URGENCY_OPTIONS, URGENCY_LABELS, srTypeLabel } from "./srLabels";

/**
 * Referral requests (/app/requests) — R9 / E §6. Submit on top (client
 * picker bound to their own book — never a typed name, never an ObjectId
 * on screen), "My filings" below. Failure is always visible; the silent
 * 400 class of bug ends here.
 *
 * Urgency has no backend field yet (flagged in the report): non-default
 * urgency prefixes the message ("[This week] …") so staff see it today
 * and H can promote it to a real field later.
 */

const CATEGORIES = ["Billing", "Policy change", "Claim question", "General"];

const URGENCIES = [
  { value: "whenever", label: "Whenever" },
  { value: "this_week", label: "This week", prefix: "[This week] " },
  { value: "urgent", label: "Urgent", prefix: "[Urgent] " }
];

function statusOf(request) {
  // v1 maps the legacy boolean; H's lifecycle field takes over when present.
  if (FEATURES.srLifecycle && request.status) {
    const map = {
      new: { label: "Open", variant: "info" },
      in_progress: { label: "In progress", variant: "warning" },
      waiting_on_client: { label: "Waiting on you", variant: "warning" },
      closed: { label: "Closed", variant: "neutral" }
    };
    return map[request.status] || { label: request.status, variant: "neutral" };
  }
  return request.completed
    ? { label: "Closed", variant: "neutral" }
    : { label: "Open", variant: "info" };
}

function RequestsPage() {
  const toast = useToast();
  const { identity } = useAuth();
  const { loading, households, requests, refresh } = useBookData();

  const [showForm, setShowForm] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [category, setCategory] = useState(FEATURES.srFields ? "general" : CATEGORIES[0]);
  const [urgency, setUrgency] = useState(FEATURES.srFields ? "normal" : "whenever");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const clientOptions = useMemo(
    () =>
      households.map((h) => ({
        value: h.id,
        label: h.name,
        description: [h.client.email, h.client.telephone].filter(Boolean).join(" · ")
      })),
    [households]
  );

  const myFilings = useMemo(
    () => [...requests].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
    [requests]
  );

  const submit = async () => {
    if (!clientId) {
      toast.error("Pick which client this is for.");
      return;
    }
    if (!description.trim()) {
      toast.error("Describe what the client needs.");
      return;
    }
    const household = households.find((h) => h.id === clientId);
    setSubmitting(true);
    try {
      const payload = {
        type: category,
        name: household ? household.name : "Client",
        client: clientId,
        email: household && household.client.email ? household.client.email : undefined,
        phone: household && household.client.telephone ? household.client.telephone : undefined,
        requester: identity ? String(identity._id) : undefined,
        requesterID: identity ? String(identity._id) : ""
      };
      if (FEATURES.srFields) {
        // Real fields: urgency is its own value, message is clean (no prefix).
        payload.urgency = urgency;
        payload.message = description.trim();
      } else {
        // Legacy: urgency folded into the message as a prefix.
        const urgencyDef = URGENCIES.find((u) => u.value === urgency);
        payload.message = `${urgencyDef.prefix || ""}${description.trim()}`;
      }
      await API.saveServiceRequest(payload);
      toast.success("Sent to the service team — we'll reply here and by email.");
      setShowForm(false);
      setClientId(null);
      setCategory(FEATURES.srFields ? "general" : CATEGORIES[0]);
      setUrgency(FEATURES.srFields ? "normal" : "whenever");
      setDescription("");
      refresh();
    } catch (err) {
      toast.error("The request didn't send — nothing was lost, try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="shell-page feat-home">
      <div className="shell-page__titlerow">
        <h1 className="shell-page__title">Requests</h1>
        {!showForm ? (
          <Button variant="primary" icon={<PlusIcon size={14} />} onClick={() => setShowForm(true)}>
            New request
          </Button>
        ) : null}
      </div>

      {showForm ? (
        <Card title="New service request">
          <Field label="Client" required htmlFor="sr-client">
            <Combobox
              options={clientOptions}
              value={clientId}
              onChange={setClientId}
              placeholder="Search your book…"
              emptyMessage="No one in your book matches that."
            />
          </Field>
          <Field
            label="Category"
            htmlFor="sr-category"
            help={
              FEATURES.srFields
                ? "Billing or claim question? Choose General question — the service team routes it."
                : undefined
            }
          >
            <Select id="sr-category" value={category} onChange={(e) => setCategory(e.target.value)}>
              {(FEATURES.srFields ? SR_TYPES : CATEGORIES.map((c) => ({ value: c, label: c }))).map(
                (o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                )
              )}
            </Select>
          </Field>
          <Field label="How soon?" htmlFor="sr-urgency">
            <Segmented
              aria-label="How soon"
              options={(FEATURES.srFields ? URGENCY_OPTIONS : URGENCIES).map(
                ({ value, label }) => ({
                  value,
                  label
                })
              )}
              value={urgency}
              onChange={setUrgency}
            />
          </Field>
          <Field label="What do they need?" required htmlFor="sr-desc">
            <TextArea
              id="sr-desc"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Needs proof of insurance for a new lease."
            />
          </Field>
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            <Button variant="primary" loading={submitting} onClick={submit}>
              Send request
            </Button>
            <Button variant="ghost" onClick={() => setShowForm(false)} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </Card>
      ) : null}

      <Card title="My filings">
        {loading ? null : myFilings.length === 0 ? (
          <EmptyState
            icon={<ServiceIcon size={32} />}
            title="No requests yet"
            message="If a client needs anything — billing, changes, questions — file it here and the service team takes it."
          />
        ) : (
          <div className="feat-feed">
            {myFilings.map((request) => {
              const status = statusOf(request);
              return (
                <div key={request._id} className="feat-feed__row" style={{ cursor: "default" }}>
                  <Badge variant={status.variant} dot>
                    {status.label}
                  </Badge>
                  <span className="feat-feed__main">
                    <span className="feat-feed__title">
                      {request.name} · {srTypeLabel(request.type)}
                    </span>
                    <span className="feat-feed__meta">
                      filed {formatDate(request.created_at)}
                      {request.urgency
                        ? ` · ${URGENCY_LABELS[request.urgency] || request.urgency}`
                        : ""}{" "}
                      — {request.message}
                    </span>
                    {request.agentVisibleReply ? (
                      /* E §6: the client-visible staff reply, quoted. Shipped
                         as a FIELD on the SR (H Tranche 3). */
                      <span className="feat-sr-reply">“{request.agentVisibleReply}”</span>
                    ) : null}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

export default RequestsPage;
